/**
 * IndexingSourceCards (TASK-263 — Per-source cards)
 *
 * A row of cards, one per data source involved in the latest indexing run.
 * Each card shows what services were extracted from that source (name / type,
 * a "has prices" indicator) plus warning badges when the source has `issues`
 * (empty pricing / generic names / no services extracted — see TASK-263 AC-1).
 *
 * The indexing run is org-wide, but artifacts carry source attribution
 * (scope/named carry source_id), which is enough to split per-source.
 *
 * States:
 *   - sources === [] → nothing (placeholder); must not crash.
 *   - otherwise      → one card per source.
 */
import { FileSpreadsheet, Globe, BookOpen, Tag, AlertTriangle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type {
  IndexingSourceSummary,
  IndexingSourceServiceSummary,
} from '@/types/indexingRun';

export interface IndexingSourceCardsProps {
  sources: IndexingSourceSummary[];
}

// ── Source-type visuals ──────────────────────────────────────────────────────
function SourceTypeIcon({ type }: { type: string }): JSX.Element {
  const t = type.toUpperCase();
  if (t === 'EXCEL') return <FileSpreadsheet className="h-4 w-4 text-green-600" />;
  if (t === 'WEB') return <Globe className="h-4 w-4 text-blue-600" />;
  return <BookOpen className="h-4 w-4 text-slate-500" />;
}

// ── Single service row ───────────────────────────────────────────────────────
function ServiceRow({
  service,
  index,
}: {
  service: IndexingSourceServiceSummary;
  index: number;
}): JSX.Element {
  return (
    <li
      data-testid={`indexing-source-service-${index}`}
      data-has-pricing={service.has_pricing ? 'true' : 'false'}
      className="flex items-center gap-2 text-sm text-gray-700">
      <span className="flex-1 truncate">{service.name}</span>
      <span className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
        {service.type}
      </span>
      {service.has_pricing && (
        <span
          title={`Есть цены (${service.pricing_count})`}
          className="inline-flex items-center gap-0.5 rounded bg-emerald-100 px-1.5 py-0.5 text-[11px] font-medium text-emerald-700">
          <Tag className="h-3 w-3" />₽
        </span>
      )}
    </li>
  );
}

// ── Single source card ───────────────────────────────────────────────────────
function SourceCard({ source }: { source: IndexingSourceSummary }): JSX.Element {
  const hasIssues = source.issues.length > 0;

  return (
    <div
      data-testid={`indexing-source-card-${source.data_source_id}`}
      data-has-issues={hasIssues ? 'true' : 'false'}
      className={cn(
        'flex flex-col gap-3 rounded-lg border p-4',
        hasIssues
          ? 'border-amber-300 bg-amber-50'
          : 'border-gray-200 bg-white'
      )}>
      {/* Header: name + type. */}
      <div className="flex items-center gap-2">
        <SourceTypeIcon type={source.type} />
        <p className="flex-1 truncate text-sm font-medium text-gray-900">
          {source.name}
        </p>
        <span className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
          {source.type}
        </span>
      </div>

      {/* Metrics: scope_count + number of services. */}
      <p className="text-xs text-gray-500">
        Скоупов: {source.scope_count} · Услуг: {source.services.length}
      </p>

      {/* Service list. */}
      {source.services.length > 0 && (
        <ul className="space-y-1.5">
          {source.services.map((service, index) => (
            <ServiceRow key={index} service={service} index={index} />
          ))}
        </ul>
      )}

      {/* Issue badges (warning style). */}
      {hasIssues && (
        <div className="flex flex-col gap-1.5">
          {source.issues.map((issue, index) => (
            <Badge
              key={index}
              data-testid="indexing-source-issue"
              variant="pending"
              className="w-fit gap-1 whitespace-normal text-left">
              <AlertTriangle className="h-3 w-3 shrink-0" />
              {issue}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Row of source cards ──────────────────────────────────────────────────────
export function IndexingSourceCards({
  sources,
}: IndexingSourceCardsProps): JSX.Element | null {
  // No per-source data → render nothing (placeholder); must not crash.
  if (sources.length === 0) {
    return null;
  }

  return (
    <div
      data-testid="indexing-source-cards"
      className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {sources.map((source) => (
        <SourceCard key={source.data_source_id} source={source} />
      ))}
    </div>
  );
}

export default IndexingSourceCards;
